import React from 'react';
import { useAppSelector } from '@/store/hooks';
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Zap, ArrowRight, Info } from "lucide-react";
import { PositionType, DcaRound } from '../types';

interface BotConfirmationProps {
  symbol: string;
  positionType: PositionType;
  leverage: number;
  entryCount: number;
  totalMargin: number;
  takeProfit: number;
  stopLoss: boolean;
  stopLossValue: number;
  rounds: DcaRound[];
  fee_pct?: number;
  limit_fee_pct?: number;
  principalAmount: number;
  multiplier: number;
  interval: number;
  useMaFilter: boolean;
  useRsiEntry: boolean;
  useRsiScaling: boolean;
  stopBotOnSL: boolean;
  marginMode: string;
  balanceLimit: number;
  useAllBalance: boolean;
  onBack: () => void;
  isLive?: boolean;
  tpThresholdStep?: number;
  tpThresholdPct?: number;
  useLimitTp?: boolean;
  pullbackPct?: number;
  exchange?: string;
  suspendedSides?: string[];
  useTestnet?: boolean;
  useProfitOffsetting?: boolean;
  profitOffsetPct?: number;
  use1hTrendFilter?: boolean;
  use1hTrendSl?: boolean;
  use1hTrendBe?: boolean;
  onConfirm: () => void;
}

export const BotConfirmation: React.FC<BotConfirmationProps> = ({
  symbol, positionType, leverage, entryCount, totalMargin, takeProfit, stopLoss, stopLossValue, rounds, fee_pct,
  principalAmount, multiplier, interval, useMaFilter, useRsiEntry, useRsiScaling, stopBotOnSL, marginMode, balanceLimit, useAllBalance,
  onBack, isLive, tpThresholdStep, tpThresholdPct,
  useLimitTp, pullbackPct, exchange, suspendedSides, useTestnet,
  useProfitOffsetting, profitOffsetPct, onConfirm, limit_fee_pct, use1hTrendFilter, use1hTrendSl, use1hTrendBe
}) => {
  const isPrivate = useAppSelector((state) => state.ui.isPrivate);
  const calculateScenarios = () => {
    let cumulativeCost = 0;
    return rounds.map((r) => {
      const roundCost = r.qty;
      cumulativeCost += roundCost;
      const roundMargin = roundCost * leverage;
      const cumulativeMarginTotal = cumulativeCost * leverage;
      
      return {
        id: r.id,
        qty: roundMargin,
        roundCost: roundCost,
        cumulativeCost,
        cumulativeMarginTotal: cumulativeMarginTotal,
        distance: Number(r.distance)
      };
    });
  };

  const scenarios = calculateScenarios();
  return (
    <div className="max-w-4xl mx-auto w-full animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12 transition-colors duration-300">
      <Card className="border border-border shadow-2xl rounded-3xl overflow-hidden bg-card">
        <CardHeader className="bg-primary/10 border-b border-border p-8 relative">
          <div className="text-center space-y-2 pt-2">
            <div className="flex items-center justify-center gap-2">
              <Badge className="bg-foreground/10 text-foreground border-none font-bold px-3 py-1">FINAL CHECK</Badge>
              {!isLive ? (
                <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-600/20 font-black px-3 py-1 uppercase animate-pulse">Paper Trading</Badge>
              ) : (
                <Badge variant="outline" className={`font-black px-3 py-1 uppercase ${useTestnet ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' : 'bg-primary/10 text-primary border-primary/20'}`}>
                  {useTestnet ? 'Testnet' : 'Mainnet'}
                </Badge>
              )}
            </div>
            <CardTitle className="text-2xl font-black tracking-tight text-foreground">봇 실행 확인</CardTitle>
            <CardDescription className="text-muted-foreground font-medium">설정하신 전략을 마지막으로 확인해주세요.</CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="p-8 space-y-6">
          {/* Main Info */}
          <div className="flex flex-col md:flex-row justify-between items-center p-6 bg-muted/30 rounded-2xl border border-border gap-4">
            <div className="flex-1 space-y-1">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">EXCHANGE & PAIR</p>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 font-black px-2 py-0.5 uppercase text-[10px]">{exchange || 'binance'}</Badge>
                <h3 className="text-xl font-black text-foreground">{symbol}</h3>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge className={`text-sm font-bold px-4 py-1.5 rounded-full ${positionType === 'LONG' ? 'bg-success/10 text-success' : positionType === 'SHORT' ? 'bg-danger/10 text-danger' : 'bg-primary/10 text-primary'}`}>
                {positionType}
              </Badge>
              {positionType === 'BOTH' && suspendedSides && suspendedSides.length > 0 && (
                <div className="flex flex-col items-end">
                  {suspendedSides.includes('long') && <span className="text-[9px] font-bold text-danger uppercase opacity-70">Long 비활성(Disabled)</span>}
                  {suspendedSides.includes('short') && <span className="text-[9px] font-bold text-danger uppercase opacity-70">Short 비활성(Disabled)</span>}
                </div>
              )}
            </div>
          </div>

          {/* Detailed Strategy Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Asset & Margin Info */}
            <div className="space-y-3">
              <h4 className="text-[11px] font-black text-muted-foreground uppercase tracking-widest px-1">Asset & Margin</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-muted/20 rounded-2xl border border-border">
                  <p className="text-[9px] font-bold text-muted-foreground uppercase">1회차 증거금 (사용자산)</p>
                  <p className="text-lg font-black text-foreground">{isPrivate ? '****' : `$${(rounds[0]?.qty || 0).toFixed(2)}`}</p>
                </div>
                <div className="p-4 bg-muted/20 rounded-2xl border border-border">
                  <p className="text-[9px] font-bold text-muted-foreground uppercase">Leverage</p>
                  <p className="text-lg font-black text-foreground">{leverage}x <span className="text-[10px] text-muted-foreground font-bold italic">({marginMode?.toUpperCase()})</span></p>
                </div>
                <div className="p-4 bg-muted/20 rounded-2xl border border-border">
                  <p className="text-[9px] font-bold text-muted-foreground uppercase">전략 총 예산 (Budget)</p>
                   <p className="text-lg font-black text-foreground">{isPrivate ? '****' : `$${(principalAmount || 0).toFixed(2)}`}</p>
                </div>
                <div className="p-4 bg-primary/5 rounded-2xl border border-primary/10">
                  <p className="text-[9px] font-bold text-primary uppercase">총 진입 규모 (Notional)</p>
                  <p className="text-lg font-black text-primary">{isPrivate ? '****' : `$${totalMargin.toLocaleString()}`}</p>
                </div>
              </div>
            </div>

            {/* Strategy & Safety Info */}
            <div className="space-y-3">
              <h4 className="text-[11px] font-black text-muted-foreground uppercase tracking-widest px-1">Strategy & Safety</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-muted/20 rounded-2xl border border-border">
                  <p className="text-[9px] font-bold text-muted-foreground uppercase">Steps / Multiplier</p>
                  <p className="text-lg font-black text-foreground">{entryCount}R / x{multiplier}</p>
                </div>
                <div className="p-4 bg-muted/20 rounded-2xl border border-border">
                  <p className="text-[9px] font-bold text-muted-foreground uppercase">Take Profit</p>
                  <p className="text-lg font-black text-success">{takeProfit}%</p>
                </div>
                <div className="p-4 bg-muted/20 rounded-2xl border border-border">
                  <p className="text-[9px] font-bold text-muted-foreground uppercase">Stop Loss</p>
                  <p className="text-lg font-black text-danger">{stopLoss ? `${stopLossValue}%` : 'OFF'}</p>
                </div>
                <div className="p-4 bg-muted/20 rounded-2xl border border-border">
                   <p className="text-[9px] font-bold text-muted-foreground uppercase">Commission (M/L)</p>
                   <p className="text-lg font-black text-amber-500">{fee_pct}% / {limit_fee_pct}%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters & Detailed Options */}
          <div className="p-4 bg-muted/10 rounded-2xl border border-border flex flex-wrap gap-4">
             {[
               { label: 'MA Filter', active: useMaFilter },
               { label: 'RSI (Entry)', active: useRsiEntry },
               { label: 'RSI (Scaling)', active: useRsiScaling },
               { label: '1h Trend', active: use1hTrendFilter },
               { label: '1h Trend SL', active: use1hTrendSl },
               { label: '1h Trend BE', active: use1hTrendBe },
               ...(positionType === 'BOTH' ? [{ label: 'Offsetting', active: useProfitOffsetting, detail: `${profitOffsetPct}%` }] : []),
               { label: 'Stop on SL', active: stopBotOnSL }
             ].map((f: any, i) => (
               <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-background rounded-xl border border-border">
                 <div className={`w-1.5 h-1.5 rounded-full ${f.active ? 'bg-success shadow-[0_0_6px_rgba(34,197,94,0.5)]' : 'bg-muted-foreground/30'}`}></div>
                 <span className="text-[10px] font-bold text-foreground">{f.label}:</span>
                 <span className={`text-[10px] font-black ${f.active ? 'text-success' : 'text-muted-foreground'}`}>
                   {f.active ? (f.detail ? `ON (${f.detail})` : 'ON') : 'OFF'}
                 </span>
               </div>
             ))}
          </div>

          {/* Scenario Preview */}
          <div className="space-y-4">
            <h4 className="text-sm font-black text-foreground uppercase tracking-tight flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500 fill-amber-500" />봇 동작 예상 시나리오
            </h4>
            <div className="bg-slate-900 dark:bg-slate-800 rounded-3xl p-6 text-white overflow-hidden relative group shadow-xl">
              <div className="relative z-10 space-y-5">
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-success shadow-[0_0_12px_rgba(16,185,129,0.8)]"></div>
                    <div className="w-0.5 h-10 border-l border-white/20 my-1 border-dashed"></div>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] bg-success/20 text-success px-2 py-0.5 rounded-full font-black uppercase">Start</span>
                      <span className="text-sm font-black">최초 진입 실행</span>
                    </div>
                    <p className="text-xs text-slate-400 font-medium">현재 시장가로 즉시 전략을 개시합니다.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.8)]"></div>
                    <div className="w-0.5 h-14 border-l border-white/20 my-1 border-dashed"></div>
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2">
                       <span className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full font-black uppercase">DCA</span>
                       <span className="text-sm font-black">분할 매수 대기 (1~{entryCount}회차)</span>
                    </div>
                    <div className="flex gap-2">
                      {Array.from({ length: Math.min(entryCount, 7) }).map((_, i) => (
                        <div key={i} className="h-5 w-1.5 bg-white/10 rounded-full group-hover:bg-amber-400/40 transition-all duration-300"></div>
                      ))}
                      {entryCount > 7 && <span className="text-[10px] text-slate-500 self-end font-bold">+{entryCount - 7} more</span>}
                    </div>
                    <p className="text-xs text-slate-400 font-medium tracking-tight">가격 하락 시 정해진 설정값에 따라 물타기를 자동 수행합니다.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-400 shadow-[0_0_12px_rgba(96,165,250,0.6)]"></div>
                  </div>
                  <div className="space-y-1.5">
                      <div className="flex justify-between items-center py-2">
                        <span className="text-xs text-muted-foreground font-medium">익절 목표 (ROE)</span>
                        <div className="text-right">
                          <span className="text-sm font-black text-primary">{takeProfit}%</span>
                          {tpThresholdStep && tpThresholdStep > 0 && (
                            <div className="text-[9px] font-bold text-primary/60 mt-0.5">
                              ({tpThresholdStep}단계부터 {tpThresholdPct}%)
                            </div>
                          )}
                        </div>
                      </div>
                    <p className="text-xs text-slate-400 font-medium">평균 단가 대비 목표 수익 도달 시 모든 포지션을 일괄 종료합니다.</p>
                  </div>
                </div>
              </div>
              <div className="absolute top-0 right-0 w-48 h-48 bg-success/5 blur-[80px] rounded-full translate-x-1/2 -translate-y-1/2"></div>
            </div>
          </div>

          {/* Scenario Table (Detailed) */}
          <div className="space-y-4 pt-4 border-t border-border">
            <h4 className="text-sm font-black text-foreground uppercase tracking-tight flex items-center gap-2">
              <Info className="w-4 h-4 text-primary" />회차별 상세 시나리오 (예상)
            </h4>
            <div className="rounded-2xl border border-border overflow-hidden shadow-sm bg-card">
              <table className="w-full text-left border-collapse">
                <thead className="bg-muted/30 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 font-bold text-muted-foreground text-[11px] uppercase">회차</th>
                    <th className="px-4 py-3 font-bold text-muted-foreground text-[11px] uppercase">진입 기준 (%)</th>
                    <th className="px-4 py-3 font-bold text-muted-foreground text-[11px] uppercase">1회차 증거금</th>
                    <th className="px-4 py-3 font-bold text-muted-foreground text-[11px] uppercase">누적 증거금</th>
                    <th className="px-4 py-3 font-bold text-muted-foreground text-[11px] uppercase px-4">포지션 규모</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border font-medium">
                  {scenarios.map((s, idx) => {
                    const isLong = positionType === 'LONG';
                    const dist = Number(s.distance) || 0;
                    const diffPct = idx === 0 ? "0.00" : (isLong ? `-${dist.toFixed(2)}` : `+${dist.toFixed(2)}`);
                    return (
                      <tr key={s.id} className="hover:bg-muted/10 transition-colors">
                        <td className="px-4 py-3 font-black text-muted-foreground/60 text-xs">#{s.id}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-md text-[11px] font-black ${idx === 0 ? 'bg-muted text-muted-foreground' : (isLong ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger')}`}>
                            {diffPct}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs font-bold text-foreground">{isPrivate ? '****' : s.roundCost.toFixed(2)} <span className="text-[10px] text-muted-foreground font-medium">{isPrivate ? '' : `(${s.qty.toLocaleString()})`}</span></td>
                        <td className="px-4 py-3 text-xs font-black text-foreground">{isPrivate ? '****' : s.cumulativeCost.toFixed(2)}</td>
                        <td className="px-4 py-3 text-xs font-black text-primary">{isPrivate ? '****' : s.cumulativeMarginTotal.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-muted-foreground font-medium italic leading-tight">* 실제 체결 시 시장가 슬리피지 등에 의해 소폭의 오차가 발생할 수 있습니다.</p>
          </div>

          <div className="bg-amber-500/10 p-4 rounded-xl border border-amber-500/20 flex gap-3 text-amber-500">
            <Info className="w-5 h-5 shrink-0 mt-0.5" />
            <p className="text-xs font-medium leading-relaxed">
              [봇 생성 완료]를 누르면 봇 생성이 완료됩니다. 이후 운용중인 봇 목록에서 [가동 버튼]을 누르면 봇이 실제 시작됩니다.<br/>
              시스템 장애/회원 부주의 등으로 인한 손실은 책임지지 않습니다. 
            </p>
          </div>
        </CardContent>

        <CardFooter className="p-8 pt-0 flex gap-4">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="flex-1 py-8 text-base font-bold border-2 border-border text-muted-foreground rounded-2xl hover:bg-muted transition-all active:scale-[0.98] bg-background"
          >
            취소
          </Button>
          <Button 
            onClick={onConfirm}
            className="flex-1 py-8 text-base font-black bg-success hover:opacity-90 text-white rounded-2xl shadow-xl group relative overflow-hidden transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <div className="relative z-10 flex items-center justify-center gap-2">
              <Zap className="w-5 h-5 fill-white text-amber-400" />
              봇 생성 완료하기
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </Button>
        </CardFooter>
      </Card>

      <p className="text-center mt-6 text-muted-foreground text-xs font-medium">안전한 자산 관리를 위해 DCA 전략을 권장합니다.</p>
    </div>
  );
};
